import * as React from 'react';

import Select from '../Select/Select';

import { daysInMonth, isValidDateOfBirth } from './helpers';

import type { IDateOfBirthValue } from './types';

export { isValidDateOfBirth };
export type { IDateOfBirthValue };

interface IDateOfBirthInputProps {
  value: IDateOfBirthValue | null;
  onChange: (next: IDateOfBirthValue | null) => void;
  isInvalid?: boolean;
  isRequired?: boolean;
  hint?: string;
}

const MONTHS: { id: string; label: string }[] = [
  { id: '1', label: 'January' },
  { id: '2', label: 'February' },
  { id: '3', label: 'March' },
  { id: '4', label: 'April' },
  { id: '5', label: 'May' },
  { id: '6', label: 'June' },
  { id: '7', label: 'July' },
  { id: '8', label: 'August' },
  { id: '9', label: 'September' },
  { id: '10', label: 'October' },
  { id: '11', label: 'November' },
  { id: '12', label: 'December' },
];

const getDayItems = (maxDay: number): { id: string; label: string }[] =>
  Array.from({ length: maxDay }, (_, i) => ({ id: String(i + 1), label: String(i + 1) }));

// Fixed loved-one DOB window per the onboarding spec — intentionally excludes
// anyone born in 2000 or later (they aren't the seniors this flow serves).
const EARLIEST_BIRTH_YEAR = 1917;
const LATEST_BIRTH_YEAR = 1999;

const getYearItems = (): { id: string; label: string }[] => {
  const items: { id: string; label: string }[] = [];
  for (let y = LATEST_BIRTH_YEAR; y >= EARLIEST_BIRTH_YEAR; y--) {
    items.push({ id: String(y), label: String(y) });
  }
  return items;
};

/**
 * Month / Day / Year `Select` triad assembled to an `IDateOfBirthValue`.
 * The Day list tracks the selected month (and year, for February) so only real
 * days are offered — 30 for April, 28/29 for February, etc. Picking a shorter
 * month (or a non-leap year against a chosen Feb 29) clears the now-impossible
 * day, so the value never describes a date the dropdowns wouldn't let you
 * build. {@link isValidDateOfBirth} remains available as a submit-time guard.
 */
const DateOfBirthInput = ({ value, onChange, isInvalid, isRequired, hint }: IDateOfBirthInputProps) => {
  const years = React.useMemo(() => getYearItems(), []);

  const month = value?.month ?? 0;
  const year = value?.year ?? 0;
  // Before a month is picked (month=0) offer the full 1..31; a year of 0 keeps
  // February permissive at 29 until a real year is chosen (see daysInMonth).
  const maxDay = month >= 1 && month <= 12 ? daysInMonth(month, year) : 31;
  const days = React.useMemo(() => getDayItems(maxDay), [maxDay]);

  const updatePart = (part: keyof IDateOfBirthValue, raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const base: IDateOfBirthValue = value ?? { month: 0, day: 0, year: 0 };
    const next: IDateOfBirthValue = { ...base, [part]: parsed };
    // A shorter month (or a non-leap year against a chosen Feb 29) can't hold
    // the picked day — drop it so the value never describes an impossible date.
    if ((part === 'month' || part === 'year') && next.day > 0) {
      const nextMax = next.month >= 1 && next.month <= 12 ? daysInMonth(next.month, next.year) : 31;
      if (next.day > nextMax) {
        next.day = 0;
      }
    }
    onChange(next);
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <span className="text-secondary flex items-center gap-0.5 text-sm font-bold">
        Date of birth
        {isRequired && <span className="text-brand-tertiary">*</span>}
      </span>
      <div className="flex w-full gap-3">
        <div className="flex-1">
          <Select
            aria-label="Month"
            placeholder="Month"
            size="md"
            items={MONTHS}
            selectedKey={value?.month ? String(value.month) : null}
            onSelectionChange={(key) => updatePart('month', String(key))}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>
        <div className="w-20 shrink-0">
          <Select
            aria-label="Day"
            placeholder="Day"
            size="md"
            items={days}
            selectedKey={value?.day && value.day <= maxDay ? String(value.day) : null}
            onSelectionChange={(key) => updatePart('day', String(key))}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>
        <div className="w-24 shrink-0">
          <Select
            aria-label="Year"
            placeholder="Year"
            size="md"
            items={years}
            selectedKey={value?.year ? String(value.year) : null}
            onSelectionChange={(key) => updatePart('year', String(key))}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>
      </div>
      {isInvalid && hint && <span className="text-error-primary text-sm">{hint}</span>}
    </div>
  );
};

DateOfBirthInput.displayName = 'DateOfBirthInput';

export default DateOfBirthInput;
